<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { bemBuilder, addressIntoGMapsLink } from "@chatfood/core-utils";
import {
  AtomIcon,
  AtomLoadingSpinner,
  AtomText,
  SatButton,
} from "@chatfood/design-system";
import type { ILocationInfoProps } from "./props";
import { useLocale } from "~/composables/use-locale";
import { useBusinessStore } from "~/stores/business";
import { useBugReporter, useDirectBackend } from "#imports";
import type { IDomainOutletState } from "~/domain/outlet-state";

const css = bemBuilder("location-info");

const { outletRepo } = useDirectBackend();

const props = defineProps<ILocationInfoProps>();
const emit = defineEmits<{
  close: [];
}>();
const business = useBusinessStore();

const isLoading = ref(false);
const outlet = ref<Awaited<ReturnType<typeof outletRepo.getOutletById>>>();

const { t } = useLocale();

const { report } = useBugReporter();

const orderedDays: Record<string, string> = {
  Mon: t("components.location_info.days_of_week.monday"),
  Tue: t("components.location_info.days_of_week.tuesday"),
  Wed: t("components.location_info.days_of_week.wednesday"),
  Thu: t("components.location_info.days_of_week.thursday"),
  Fri: t("components.location_info.days_of_week.friday"),
  Sat: t("components.location_info.days_of_week.saturday"),
  Sun: t("components.location_info.days_of_week.sunday"),
};
const dayOrder = Object.keys(orderedDays);

type GroupedOpeningHour = {
  dayOfWeek: string;
  times: { start: string; end: string }[];
};

type ResponseOpeningHours = Awaited<
  ReturnType<typeof outletRepo.getSettings>
>[0]["openingHours"];

const areSameTimesSlots = (
  times1: { start: string; end: string }[],
  times2: { start: string; end: string }[],
): boolean => {
  if (times1.length !== times2.length) return false;
  return times1.every(
    (time, idx) =>
      time.start === times2[idx].start && time.end === times2[idx].end,
  );
};

const consolidateConsecutiveDays = (
  groupedHours: Array<{
    dayOfWeek: string;
    times: { start: string; end: string }[];
  }>,
): GroupedOpeningHour[] => {
  if (groupedHours.length === 0) return [];

  const result: GroupedOpeningHour[] = [];
  let rangeStart = 0;
  let rangeEnd = 0;

  for (let i = 1; i < groupedHours.length; i++) {
    const currentDayIndex = dayOrder.indexOf(groupedHours[i].dayOfWeek);
    const prevDayIndex = dayOrder.indexOf(groupedHours[i - 1].dayOfWeek);
    const isConsecutive = currentDayIndex === prevDayIndex + 1;

    if (
      !areSameTimesSlots(
        groupedHours[i].times,
        groupedHours[rangeStart].times,
      ) ||
      !isConsecutive
    ) {
      // Times are different or days are not consecutive, so close the current range
      if (rangeStart === rangeEnd) {
        // Single day
        result.push({
          dayOfWeek: orderedDays[groupedHours[rangeStart].dayOfWeek],
          times: groupedHours[rangeStart].times,
        });
      } else {
        // Range of days
        const startDay = orderedDays[groupedHours[rangeStart].dayOfWeek];
        const endDay = orderedDays[groupedHours[rangeEnd].dayOfWeek];
        result.push({
          dayOfWeek: `${startDay}-${endDay}`,
          times: groupedHours[rangeStart].times,
        });
      }
      // Start a new range
      rangeStart = i;
      rangeEnd = i;
    } else {
      // Same times and consecutive days, extend the range
      rangeEnd = i;
    }
  }

  // Push the last range
  if (rangeStart === rangeEnd) {
    result.push({
      dayOfWeek: orderedDays[groupedHours[rangeStart].dayOfWeek],
      times: groupedHours[rangeStart].times,
    });
  } else {
    const startDay = orderedDays[groupedHours[rangeStart].dayOfWeek];
    const endDay = orderedDays[groupedHours[rangeEnd].dayOfWeek];
    result.push({
      dayOfWeek: `${startDay}-${endDay}`,
      times: groupedHours[rangeStart].times,
    });
  }

  return result;
};

const groupOpeningHours = (
  openingHours: ResponseOpeningHours,
): GroupedOpeningHour[] => {
  const map = new Map<string, { start: string; end: string }[]>();

  for (const { dayOfWeek, startTime, endTime } of openingHours) {
    const times = map.get(dayOfWeek) ?? [];
    times.push({ start: startTime, end: endTime });
    map.set(dayOfWeek, times);
  }

  const groupedDelivery = Array.from(map.entries()).map(
    ([dayOfWeek, times]) => ({
      dayOfWeek,
      times: times.sort((cur, acc) => cur.start.localeCompare(acc.start)),
    }),
  );

  const sortedByDay = [...groupedDelivery].sort(
    (cur, acc) =>
      dayOrder.indexOf(cur.dayOfWeek) - dayOrder.indexOf(acc.dayOfWeek),
  );

  return consolidateConsecutiveDays(sortedByDay);
};

const openingHours = ref<GroupedOpeningHour[]>([]);

const fetchOutletData = async (outletId: string) => {
  if (!outletId) {
    outlet.value = undefined;
    return;
  }
  isLoading.value = true;

  try {
    const [outletDetails, orderingModeSettings] = await Promise.all([
      outletRepo.getOutletById({
        outletId,
        salesChannel: business.salesChannel,
        orderingMode: props.orderingMode,
      }),
      outletRepo.getSettings({
        outletId,
        salesChannel: business.salesChannel,
        orderingMode: [props.orderingMode],
      }),
    ]);

    outlet.value = outletDetails;

    const [modeSetting] = orderingModeSettings;
    openingHours.value = groupOpeningHours(modeSetting?.openingHours ?? []);
  } catch (e) {
    report(e);
  } finally {
    isLoading.value = false;
  }
};

const outletStateMap: Record<IDomainOutletState, string> = {
  open: t("components.location_info.open"),
  closed: t("components.location_info.closed"),
  busy: t("components.location_info.open"),
  pre_order_only: t("components.location_info.pre_order_only"),
};

const outletStateLabel = (state: IDomainOutletState) => {
  return outletStateMap[state];
};

const stateColor = {
  open: "color-mint",
  busy: "color-mint",
  closed: "color-text-disabled",
  pre_order_only: "color-text-body",
} as const;

const mapStateColor = computed(() => {
  const state = outlet.value?.operationalState;
  return state && state in stateColor
    ? stateColor[state as keyof typeof stateColor]
    : undefined;
});

const outletStateLabelComputed = computed(() => {
  const state = outlet.value?.operationalState;
  return state ? outletStateLabel(state) : "";
});

const todayClosingTime = computed(() => {
  const today = new Date();
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const todayShortName = dayNames[today.getDay()];
  const todayFullName = orderedDays[todayShortName];

  // Find today's opening hours - could be a single day or part of a range
  const todaySlot = openingHours.value.find((slot) => {
    // Check if it's a range (e.g., "Monday-Friday")
    if (slot.dayOfWeek.includes("-")) {
      const [startDay, endDay] = slot.dayOfWeek.split("-");
      const startIndex = dayOrder.findIndex(
        (day) => orderedDays[day] === startDay,
      );
      const endIndex = dayOrder.findIndex((day) => orderedDays[day] === endDay);
      const todayIndex = dayOrder.indexOf(todayShortName);
      return todayIndex >= startIndex && todayIndex <= endIndex;
    }
    // Check if it's a single day
    return slot.dayOfWeek === todayFullName;
  });

  if (!todaySlot || todaySlot.times.length === 0) {
    return t("components.location_info.closed");
  }

  // Get the last closing time (in case there are multiple time slots)
  const lastSlot = todaySlot.times[todaySlot.times.length - 1];
  return lastSlot.end;
});

const onGetDirections = () => {
  if (!outlet.value) return;
  const link = addressIntoGMapsLink({
    lat: outlet.value.address.coordinate.lat,
    lng: outlet.value.address.coordinate.lng,
  });

  window.open(link, "_blank");
};

const onCallStore = () => {
  if (!outlet.value || !outlet.value.phoneNumber) return;
  const phoneNumber = outlet.value.phoneNumber.replace(/\s/g, "");
  window.open(`tel:${phoneNumber}`, "_self");
};

watch(
  () => [props.outletId, props.orderingMode] as const,
  ([newOutletId]) => {
    fetchOutletData(newOutletId);
  },
  { immediate: true },
);
</script>

<template>
  <div :class="css('')">
    <AtomLoadingSpinner v-if="isLoading" :class="css('loading')" />
    <div v-else :class="css('content')">
      <div :class="css('info-header')">
        <div :class="css('title')">
          <AtomText type="subtitle">{{ outlet?.name }}</AtomText>
          <AtomIcon
            :class="css('close')"
            name="close"
            size="medium"
            @click="emit('close')"
          />
        </div>
        <div>
          <AtomText data-test="operational-state" :color="mapStateColor">{{
            outletStateLabelComputed
          }}</AtomText>
          <AtomText
            v-if="
              outlet?.operationalState === 'open' ||
              outlet?.operationalState === 'busy'
            "
            color="color-text-secondary"
          >
            {{
              t("components.location_info.closing_time", {
                hour: todayClosingTime,
              })
            }}
          </AtomText>
        </div>
        <AtomText
          v-if="outlet?.operationalState === 'busy'"
          data-test="busy-warning"
          color="color-canary"
          >{{ t("components.location_info.busy_label") }}</AtomText
        >
      </div>
      <AtomText>{{ outlet?.address.location }}</AtomText>
      <div v-if="openingHours.length" :class="css('divisor')" />
      <AtomText v-if="openingHours.length" type="body-bold">
        {{ t("components.location_info.store_hours_label") }}
      </AtomText>
      <ul :class="css('list')">
        <li
          v-for="slot in openingHours"
          :key="slot.dayOfWeek"
          :class="css('list-item')"
        >
          <AtomText color="color-text-secondary"
            >{{ slot.dayOfWeek }}:</AtomText
          >
          <AtomText
            v-for="(time, index) in slot.times"
            :key="`time-${index}`"
            color="color-text-secondary"
          >
            {{ time.start }} - {{ time.end }}
          </AtomText>
        </li>
      </ul>
      <div :class="css('divisor')" />
      <div :class="css('ctas')">
        <SatButton
          v-if="outlet?.phoneNumber"
          data-test="call-store-button"
          variant="secondary"
          type="button"
          theme="filled"
          size="large"
          @click="onCallStore"
        >
          {{ t("components.location_info.call_store_cta") }}
        </SatButton>
        <SatButton
          data-test="get-directions-button"
          variant="secondary"
          type="button"
          theme="filled"
          size="large"
          @click="onGetDirections"
        >
          {{ t("components.location_info.get_directions_cta") }}
        </SatButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.location-info {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  position: relative;

  &__content {
    width: 100%;
  }

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__info-header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    margin-bottom: var(--spacing-md);
  }

  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__close {
    cursor: pointer;
  }

  &__divisor {
    height: 1px;
    background-color: var(--color-base-90);
    display: flex;
    margin: var(--spacing-xl) 0;
  }

  &__list {
    list-style: none;
    padding-left: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    margin-top: var(--spacing-sm);
  }

  &__list-item {
    display: flex;
    gap: var(--spacing-sm);
    margin-top: var(--spacing-xs);
  }

  &__ctas {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    width: 100%;
  }
}
</style>
